import React, { useState } from "react";
import { gettext } from "web/script/modules/django-i18n";
import { getURL } from "web/script/routing/getUrl";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import AppDownloadBadge from "web/react/components/app-download-badge/app-download-badge";
import { openOverlay } from "web/redux/ducks/cookie-settings";
import withReduxProvider from "web/react/redux-provider";
import { useDispatch } from "react-redux";
import CookieSettingsContainer from "web/react/components/cookie-settings-container/cookie-settings-container";
import classnames from "classnames";
import { languageIs, languageIsEnglish } from "web/script/modules/language";
import styles from "./classic-footer.css";

const WOMEN = "women";
const MEN = "men";
const HELP = "help";

interface Link {
    url: string;
    name: string;
}
interface FooterProps {
    data: {
        copyright_notice: string;
        extra_links_enabled: boolean;
        men_links_enabled: boolean;
        men_links: Link[];
        women_links_enabled: boolean;
        women_links: Link[];
        shop_gender_women_link: string;
        shop_gender_men_link: string;
    };
    helpCenterUrls: string[];
    showCookieSettingsOption: boolean;
}

interface GenderLinksProps {
    links: Link[];
    extraLinksEnabled: boolean;
    allLink: string;
}

function GenderLinks({ links, extraLinksEnabled, allLink }: GenderLinksProps): JSX.Element {
    return (
        <>
            <ul>
                {links.map((link) => (
                    <li key={link.url}>
                        <a className={styles.link} href={link.url}>
                            {link.name}
                        </a>
                    </li>
                ))}
            </ul>
            {!extraLinksEnabled && (
                <a className={styles.viewAll} href={allLink}>
                    {gettext("site_links.view_all")} {/* View All */}
                </a>
            )}
        </>
    );
}

interface FooterBlockProps {
    isOpen: boolean;
    onClick: Function;
    title: string;
    children?: object;
}

export function FooterBlock({ title, isOpen, onClick, children }: FooterBlockProps): JSX.Element {
    return (
        <div className={styles.footerBlock}>
            <h4 className={styles.footerBlockTitle}>
                <button
                    type="button"
                    className={styles.footerBlockButton}
                    onClick={(): void => onClick()}
                >
                    <span>{title}</span>
                    <SVGIcon name="chevron" className={styles.arrow} rotate={isOpen ? -90 : 90} />
                </button>
            </h4>
            <div
                className={classnames(styles.footerBlockContent, {
                    [styles.open]: isOpen,
                })}
            >
                {children}
            </div>
        </div>
    );
}

export function ClassicFooter({
    data,
    helpCenterUrls,
    showCookieSettingsOption,
}: FooterProps): JSX.Element {
    const dispatch = useDispatch();
    const [openBlockId, setOpenBlockId] = useState<string | null>(null);

    function setOpenBlock(blockId: string): void {
        if (blockId == openBlockId) {
            // close everything
            setOpenBlockId(null);
        } else {
            // Set the new active block
            setOpenBlockId(blockId);
        }
    }
    function openCookieSettingsModal(event: React.MouseEvent<HTMLAnchorElement>): void {
        event.preventDefault();
        dispatch(openOverlay());
    }

    return (
        <div className={styles.footerWrap}>
            <CookieSettingsContainer />
            <footer className={styles.footer}>
                {languageIsEnglish() && (
                    <div className={styles.leadContent}>
                        <div className={styles.appBadge}>
                            <AppDownloadBadge
                                small
                                color={"white"}
                                appDownloadLink={"footer"}
                                eventLabel={"footer_download_badge"}
                                titleId="footer.mobile.image_alt_text"
                            />
                        </div>
                        <p className={styles.appCallout}>
                            <a href="/app/" className={styles.appCalloutLink}>
                                {gettext("footer.mobile.text")}
                            </a>
                        </p>
                        <p className={styles.copyright}>{data.copyright_notice}</p>
                    </div>
                )}

                {data.women_links_enabled && (
                    <FooterBlock
                        title={gettext("footer.links_for_women.title")}
                        isOpen={openBlockId == WOMEN}
                        onClick={(): void => setOpenBlock(WOMEN)}
                    >
                        <GenderLinks
                            links={data.women_links}
                            extraLinksEnabled={data.extra_links_enabled}
                            allLink={data.shop_gender_women_link}
                        />
                    </FooterBlock>
                )}

                {data.men_links_enabled && (
                    <FooterBlock
                        title={gettext("footer.links_for_men.title")}
                        isOpen={openBlockId == MEN}
                        onClick={(): void => setOpenBlock(MEN)}
                    >
                        <GenderLinks
                            links={data.men_links}
                            extraLinksEnabled={data.extra_links_enabled}
                            allLink={data.shop_gender_men_link}
                        />
                    </FooterBlock>
                )}

                <FooterBlock
                    title={gettext("footer.help_info.title_variant_b")}
                    isOpen={openBlockId == HELP}
                    onClick={(): void => setOpenBlock(HELP)}
                >
                    <ul>
                        <li>
                            <a className={styles.link} href={helpCenterUrls["homepage"]}>
                                {gettext("footer.help_info.help_center_link")}
                                {/* Help Center */}
                            </a>
                        </li>
                        <li>
                            <a className={styles.link} href={getURL("about")}>
                                {gettext("footer.links_about_us")}
                                {/* About us */}
                            </a>
                        </li>
                        <li>
                            <a className={styles.link} href={helpCenterUrls["shipping_policy"]}>
                                {gettext("footer.help_info.shipping_policy_link")}
                                {/* Shipping Policy */}
                            </a>
                        </li>
                        <li>
                            <a className={styles.link} href={helpCenterUrls["returns_policy"]}>
                                {gettext("footer.help_info.returns_policy_link")}
                                {/* Returns Policy */}
                            </a>
                        </li>
                        {languageIsEnglish() && (
                            <>
                                <li>
                                    <a className={styles.link} href={"https://making.lyst.com/"}>
                                        {gettext("footer.help_info.developers_link")}
                                        {/* Developers */}
                                    </a>
                                </li>
                                <li>
                                    <a className={styles.link} href={helpCenterUrls["payments"]}>
                                        {gettext("footer.help_info.payments_link")}
                                    </a>
                                    {/* Payments */}
                                </li>
                                <li>
                                    <a
                                        className={styles.link}
                                        href={helpCenterUrls["refund_policy"]}
                                    >
                                        {gettext("footer.help_info.refund_policy_link")}
                                    </a>
                                    {/* Refunds Policy */}
                                </li>
                            </>
                        )}
                        <li>
                            <a className={styles.link} href={getURL("careers")}>
                                {gettext("footer.links_careers")}
                                {/* Careers */}
                            </a>
                        </li>
                        <li>
                            <a className={styles.link} href={getURL("helpContactUs")}>
                                {gettext("footer.help_info.contact_link")}
                                {/* Contact */}
                            </a>
                        </li>
                        <li>
                            <a className={styles.link} href={getURL("helpTermsAndConditions")}>
                                {gettext("footer.help_info.terms_and_conditions_link")}
                                {/* Terms & Conditions */}
                            </a>
                        </li>
                        <li>
                            <a className={styles.link} href={getURL("helpPrivacyPolicy")}>
                                {gettext("footer.help_info.privacy_and_cookie_policy_link")}
                                {/* Privacy & Cookie Policy */}
                            </a>
                        </li>
                        <li>
                            <a className={styles.link} href={getURL("helpIntellectualProperty")}>
                                {gettext("footer.help_info.intellectual_property_link")}
                                {/* Intellectual Property */}
                            </a>
                        </li>
                        {/* German language footer gets an extra link to Impressum section of the privacy policy. */}
                        {languageIs("DE") && (
                            <li>
                                <a
                                    className={styles.link}
                                    href={`${getURL("helpPrivacyPolicy")}#impressum`}
                                >
                                    {"Impressum"}
                                </a>
                            </li>
                        )}
                        <li>
                            <a className={styles.link} href={getURL("partnerContact")}>
                                {gettext("footer.help_info.become_a_partner_link")}
                                {/* Become a Partner */}
                            </a>
                        </li>
                        {languageIsEnglish() ? (
                            <>
                                <li>
                                    <a className={styles.link} href="https://www.lyst.com/news/">
                                        {gettext("footer.links_lyst_news")}
                                        {/* Lyst News */}
                                    </a>
                                </li>
                                <li>
                                    <a
                                        className={styles.link}
                                        href={getURL("sitemapsAllCategories")}
                                    >
                                        {gettext("footer.links_categories")}
                                        {/* Categories */}
                                    </a>
                                </li>
                            </>
                        ) : (
                            <li>
                                <a className={styles.link} href={getURL("articleIndex")}>
                                    {gettext("footer.links_lyst_editorial")}
                                </a>
                            </li>
                        )}
                        {showCookieSettingsOption && (
                            <li>
                                <a className={styles.link} onClick={openCookieSettingsModal}>
                                    {gettext("cookie_notification.cookie_settings.title")}
                                    {/* Cookie Settings */}
                                </a>
                            </li>
                        )}
                    </ul>
                </FooterBlock>
            </footer>
        </div>
    );
}

export default withReduxProvider(ClassicFooter);
